import { useMemo } from 'react';
import { parseEntityRef } from '@backstage/catalog-model';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { Combobox, type Option } from '@backstage/ui';
import { useSortedEntities } from './UseSortedEntities';

interface FilterBySibling {
  /** Name of the sibling parameter to read, e.g. 'systemName'. */
  field: string;
  /** The entity spec field its value is compared against, e.g. 'system'. */
  specField: string;
}

/**
 * A single-kind entity picker, sorted alphabetically by display name.
 *
 * The stock EntityPicker (and OwnerPicker, which wraps it) fetches via
 * catalogApi.streamEntities() with no ordering applied - the dropdown lands
 * in whatever order the catalog backend happens to return rows in, which is a
 * known upstream gap (backstage/backstage#31304) rather than anything this
 * app's catalog does wrong.
 *
 * Built on @backstage/ui's Combobox rather than a plain MUI Autocomplete -
 * that's the same component the scaffolder's own bui-themed EntityPicker
 * wraps internally (see @backstage/plugin-scaffolder's
 * components/fields/Autocomplete), just without public field-extension
 * access to it. Using it directly, rather than MUI, is what keeps this field
 * the same size as every other field on the form; a raw MUI Autocomplete
 * renders visibly larger. It also sidesteps the label bug the legacy MUI
 * OwnerPicker has by construction: each option's `label` is set explicitly
 * to the entity's presentation title, never derived from the entity ref -
 * an earlier version of this field got that wrong too, showing
 * "group:default/solutions" instead of "Solutions" once selected.
 *
 * `kind` comes from `ui:options.catalogFilter.kind` as a single string, not
 * EntityPicker's full filter generality - every use of this field in this
 * repo picks from exactly one kind.
 *
 * `ui:options.filterBySibling` narrows the list further, to entities whose
 * `spec[specField]` matches another parameter's CURRENT value on the same
 * form (e.g. only ServiceGroups belonging to the selected System). Read via
 * `formContext.formData`, which the Stepper keeps live - every field's
 * onChange merges straight into it (see Stepper.esm.js's handleChange), so
 * this reacts within the same step, not just across steps. `field`'s value is
 * itself a full entity ref (this field's own `onChange` shape); only its bare
 * name is compared, since that's what a spec relation field holds.
 */
export const SortedEntityField = (
  props: FieldExtensionComponentProps<string>,
) => {
  const {
    onChange,
    formData,
    schema,
    required,
    rawErrors,
    uiSchema,
    formContext,
  } = props;
  const uiOptions = uiSchema?.['ui:options'] as
    | { catalogFilter?: { kind?: string }; filterBySibling?: FilterBySibling }
    | undefined;
  const kind = uiOptions?.catalogFilter?.kind;
  const filterBySibling = uiOptions?.filterBySibling;
  const siblingValue = filterBySibling
    ? ((formContext as { formData?: Record<string, unknown> } | undefined)
        ?.formData?.[filterBySibling.field] as string | undefined)
    : undefined;
  const siblingName = siblingValue
    ? parseEntityRef(siblingValue).name
    : undefined;

  const { value: entries, loading } = useSortedEntities(
    kind,
    filterBySibling?.specField,
  );

  const visibleOptions: Option[] = useMemo(() => {
    if (loading) {
      return [{ id: '__loading__', label: 'Loading…', disabled: true }];
    }
    const filtered =
      filterBySibling && siblingName
        ? (entries ?? []).filter(entry => entry.specValue === siblingName)
        : entries ?? [];
    return filtered.map(entry => ({
      id: entry.ref,
      label: entry.label,
    }));
  }, [entries, loading, filterBySibling, siblingName]);

  return (
    <Combobox
      label={(schema.title as string) ?? kind ?? 'Value'}
      description={schema.description as string | undefined}
      isRequired={required}
      isInvalid={Boolean(rawErrors?.length)}
      options={visibleOptions}
      selectedKey={formData ?? null}
      onSelectionChange={key => onChange(key ? String(key) : undefined)}
    />
  );
};
